"use client";

import { Radio as RadioPrimitive } from "@base-ui/react/radio";
import { RadioGroup as RadioGroupPrimitive } from "@base-ui/react/radio-group";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cnState } from "@beweegbaas/ui/lib/utils";
import { InfoPopover } from "./info-popover";

type RadioTabsContextValue = {
  mode?: "tabs" | "default";
  orientation?: "horizontal" | "vertical";
};

const RadioTabsContext = React.createContext<RadioTabsContextValue>({});

const radioTabsVariants = cva("flex gap-1", {
  variants: {
    orientation: {
      horizontal: "flex-row flex-wrap",
      vertical: "flex-col",
    },
    mode: {
      tabs: "",
      default: "",
    },
  },
  compoundVariants: [
    {
      mode: "tabs",
      orientation: "horizontal",
      class: "bg-muted p-1 rounded-md min-h-9",
    },
    {
      mode: "tabs",
      orientation: "vertical",
      class: "bg-muted p-1 rounded-md",
    },
  ],
  defaultVariants: {
    orientation: "vertical",
    mode: "default",
  },
});

const radioTabVariants = cva(
  "flex items-center gap-2 text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer",
  {
    variants: {
      mode: {
        tabs: [
          "py-1 px-6 rounded grow justify-center text-muted-foreground",
          "data-checked:bg-background data-checked:text-foreground data-checked:shadow-sm",
        ],
        default: "text-foreground",
      },
      orientation: {
        horizontal: "",
        vertical: "",
      },
    },
    defaultVariants: {
      mode: "default",
      orientation: "vertical",
    },
  },
);

export interface RadioTabsProps
  extends
    Omit<RadioGroupPrimitive.Props, "children" | "onChange">,
    VariantProps<typeof radioTabsVariants> {
  description?: string;
  errorMessage?: string;
  children?: React.ReactNode;
  onChange?: (value: string | null) => void;
}

export function RadioTabs({
  description: _description,
  errorMessage: _errorMessage,
  mode = "default",
  orientation = "vertical",
  className,
  children,
  onChange,
  onValueChange,
  ...props
}: RadioTabsProps) {
  const variants = { mode, orientation };
  return (
    <RadioTabsContext.Provider
      value={{
        mode: mode ?? "default",
        orientation: orientation ?? "vertical",
      }}
    >
      <RadioGroupPrimitive
        {...props}
        onValueChange={(value, eventDetails) => {
          onValueChange?.(value, eventDetails);
          onChange?.(value as string | null);
        }}
        className={cnState(radioTabsVariants(variants), className)}
      >
        {children}
      </RadioGroupPrimitive>
    </RadioTabsContext.Provider>
  );
}

export interface RadioTabProps extends Omit<RadioPrimitive.Root.Props, "children"> {
  children?: React.ReactNode;
  tooltip?: string;
}

export function RadioTab({ children, tooltip, className, ...props }: RadioTabProps) {
  const context = React.useContext(RadioTabsContext);
  const mode = context.mode ?? "default";
  const orientation = context.orientation ?? "vertical";

  return (
    <RadioPrimitive.Root
      {...props}
      className={cnState(radioTabVariants({ mode, orientation }), className)}
    >
      {mode === "tabs" ? null : <RadioPrimitive.Indicator className="hidden" />}
      {children}
      {tooltip && <InfoPopover side="right">{tooltip}</InfoPopover>}
    </RadioPrimitive.Root>
  );
}
